import React, { useContext, useEffect, useState } from "react";
import { Link, RouteComponentProps, useHistory } from "react-router-dom";
import styled from "styled-components";
import AuthContext from "../AuthContext";
import CenterContainer from "../components/CenterContainer";
import { RawFormSelect } from "../components/FormElements";
import Title from "../components/Title";
import { EventRequest, Event, EventLocation, EventReserveRequest, removeFullEventLocations } from "consumerhouse-shared";
import { TimeTable } from "../components/TimeTable";
import { faCheck, faClock, faExclamationTriangle, faInfoCircle, faMapMarkerAlt, faPen, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import Button from "../components/Button";
import Alert from "../components/Alert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Translation, useLanguage } from "../useLanguage";
import Footer from "../components/Footer";
import { List as LoadingList } from "react-content-loader";

const Description = styled.p`
    font-size: 1.2em;
    margin: 1.7em 0;
`;

export function EventReserveComponent({ event, onApply }: { event: Event; onApply: () => void }) {
    const [location, setLocation] = useState<EventLocation | null>(null);
    const [time, setTime] = useState<Date | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const language = useLanguage();
    const { client, auth } = useContext(AuthContext);

    return (
        <>
            {auth!.user.admin && (
                <Link to={`/admin/event/${event.id}/edit`}>
                    <Button icon={faPen}>Aanpassen</Button>
                </Link>
            )}

            {/* <h2>
                            <FontAwesomeIcon icon={faInfoCircle} /> Over dit evenement
                        </h2> */}
            <Description>
                <p>{event.description}</p>
                {event.test && (
                    <p>
                        {language("event-reserve-test")} <strong>{event.test!.name}</strong>
                    </p>
                )}
            </Description>

            {event.locations!.length === 0 ? (
                <Alert icon={faExclamationTriangle} style={{ flexWrap: "nowrap", maxWidth: "500px" }} color="#e38b29" textColor="white">
                    <div>
                        <h3>{language("event-reserve-late-title")}</h3>
                        <p>{language("event-reserve-late-description")}</p>
                    </div>
                </Alert>
            ) : (
                <>
                    <h2>
                        <FontAwesomeIcon icon={faMapMarkerAlt} /> {language("event-reserve-select-location")}
                    </h2>
                    <RawFormSelect
                        style={{ margin: "0.8em 0", maxWidth: "500px", width: "100%" }}
                        onChange={(ev) => {
                            setLocation(event.locations![parseInt(ev.target.value)] ?? null);
                            setTime(null);
                        }}>
                        <option value="">{language("event-reserve-select-location-dropdown")}</option>
                        {event.locations!.map((location, i) => (
                            <option key={location.id} value={i}>
                                {location.address}
                            </option>
                        ))}
                    </RawFormSelect>
                </>
            )}

            {location && (
                <>
                    <h2>
                        <FontAwesomeIcon icon={faClock} /> {language("event-reserve-select-datetime")}
                    </h2>
                    <TimeTable
                        minDate={new Date()}
                        style={{ margin: "0.8em 0" }}
                        onSelect={(newTime) => setTime(newTime)}
                        selected={time ?? undefined}
                        dates={location.timestamps!.map((e) => new Date(e.timeStamp))}
                    />
                </>
            )}

            {time && location && (
                <>
                    <hr />
                    <Alert icon={faInfoCircle} color="#0001" textColor="black" style={{ fontSize: "1.1em", margin: "0 0 1em 0" }}>
                        <p>
                            {language("event-reserve-expect1")} <strong>{time.toLocaleDateString()}</strong> {language("event-reserve-expect2")}{" "}
                            <strong>{time.toLocaleTimeString()}</strong> {language("event-reserve-expect3")} <strong>{location.address}</strong>{" "}
                            {language("event-reserve-expect4")} <strong>{event.title}</strong>
                        </p>
                    </Alert>
                    <p>
                        <small>{language("event-reserve-footnote")}</small>
                    </p>

                    <Button
                        style={{ display: "block", margin: "1em 0" }}
                        color="#56a44f"
                        icon={faCheck}
                        disabled={!location.id || !time || loading}
                        loading={loading}
                        onClick={async () => {
                            setLoading(true);
                            await new Promise((resolve) => setTimeout(resolve, 300));
                            let res = await client.fetch("/event/reserve", { locationId: location.id!, time: time.getTime() });
                            setLoading(false);
                            if (res && res.error) {
                                if (res.error === "error-event-reserve-twice") {
                                    window.alert(language("error-event-reserve-twice"));
                                    onApply();
                                } else {
                                    setError(res.error);
                                }
                            } else {
                                onApply();
                            }
                        }}>
                        {language("event-reserve-confirm")}
                    </Button>
                </>
            )}

            {error && (
                <Alert icon={faTimesCircle}>
                    <div>
                        <p>{language(error as Translation)}</p>
                    </div>
                </Alert>
            )}
        </>
    );
}

export default function EventReserve({ match }: RouteComponentProps<{ eventId: string }>) {
    const [event, setEvent] = useState<Event | null>(null);
    const { client } = useContext(AuthContext);
    const history = useHistory();
    const language = useLanguage();

    useEffect(() => {
        (async () => {
            let res = await client.fetch("/event", { id: match.params.eventId });
            removeFullEventLocations(res.event);
            setEvent(res.event);
        })();
    }, [match.params.eventId, client]);

    return (
        <>
            <CenterContainer style={{ minHeight: "80vh", margin: "2em 0" }}>
                <Title>
                    {language("event-reserve-title")} {event?.title}
                </Title>
                {event ? <EventReserveComponent event={event} onApply={() => history.push("/account")} /> : <LoadingList />}
            </CenterContainer>
            <Footer />
        </>
    );
}
